import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userSearchService, UserSearchResult } from '../services/userSearchService';
import { SearchIcon, UsersIcon } from '../components/Icons';

const UserSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState<'ALL' | 'STUDENT' | 'TEACHER'>('ALL');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const handleSearch = async (page = 0) => {
    if (!keyword.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await userSearchService.search({
        keyword: keyword.trim(),
        type,
        page,
        size: 20,
      });
      setResults(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      setCurrentPage(page);
      setHasSearched(true);
    } catch (err: any) {
      setError(err.response?.data?.message || '搜索失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(0);
    }
  };

  const handleViewDetail = (item: UserSearchResult) => {
    if (item.type === 'STUDENT') {
      navigate(`/user-search/students/${item.id}`);
    } else if (item.schoolId) {
      navigate(`/schools/${item.schoolId}/teachers`);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-on-surface font-headline">用户管理</h1>
        <p className="text-sm text-on-surface-variant mt-1 font-body">搜索系统中的学生和教师</p>
      </div>

      {/* Search Bar */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="输入姓名或身份证号搜索..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-4 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <div className="absolute left-3 top-3 text-on-surface-variant">
              <SearchIcon size={18} />
            </div>
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'ALL' | 'STUDENT' | 'TEACHER')}
            className="px-4 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 text-sm"
          >
            <option value="ALL">全部</option>
            <option value="STUDENT">学生</option>
            <option value="TEACHER">教师</option>
          </select>
          <button
            onClick={() => handleSearch(0)}
            disabled={!keyword.trim() || loading}
            className="px-6 py-2.5 gradient-primary text-on-primary rounded-xl hover:opacity-90 transition-colors font-medium shadow-teal disabled:opacity-50 disabled:cursor-not-allowed"
          >
            搜索
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 text-danger-800 px-4 py-3 rounded-xl mb-6 shadow-soft">
          {error}
        </div>
      )}

      {/* Results */}
      {!hasSearched ? (
        <div className="text-center py-20">
          <div className="text-on-surface-variant opacity-40 mb-4 flex justify-center">
            <UsersIcon size={48} />
          </div>
          <p className="text-on-surface-variant font-body">请输入姓名或身份证号搜索用户</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-on-surface-variant font-body">搜索中...</div>
      ) : results.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-on-surface-variant opacity-40 mb-4 flex justify-center">
            <SearchIcon size={48} />
          </div>
          <p className="text-on-surface-variant font-body">未找到匹配的用户</p>
        </div>
      ) : (
        <>
          <div className="text-sm text-on-surface-variant mb-4 font-label">
            共找到 {totalElements} 条结果
          </div>
          <div className="space-y-3">
            {results.map((item) => {
              const itemKey = `${item.type}-${item.id}`;

              return (
                <div
                  key={itemKey}
                  className="bg-surface-container-lowest rounded-2xl shadow-soft hover:shadow-soft-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-lg font-semibold text-on-surface">{item.name}</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.type === 'STUDENT'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {item.typeDisplay}
                          </span>
                          {item.genderDisplay && (
                            <span className="text-sm text-on-surface-variant font-label">{item.genderDisplay}</span>
                          )}
                          {item.hasUserAccount && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-surface-container-high text-on-surface-variant font-label">已创建账号</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm font-body">
                          <div>
                            <span className="text-on-surface-variant font-label">身份证号：</span>
                            <span className="text-on-surface">{item.idCardNumber}</span>
                          </div>
                          {item.schoolName && (
                            <div>
                              <span className="text-on-surface-variant font-label">学校：</span>
                              <span className="text-on-surface">{item.schoolName}</span>
                            </div>
                          )}
                          {item.type === 'STUDENT' && item.className && (
                            <div>
                              <span className="text-on-surface-variant font-label">班级：</span>
                              <span className="text-on-surface">{item.className}</span>
                            </div>
                          )}
                          {item.type === 'STUDENT' && item.studentNumber && (
                            <div>
                              <span className="text-on-surface-variant font-label">学号：</span>
                              <span className="text-on-surface">{item.studentNumber}</span>
                            </div>
                          )}
                          {item.type === 'TEACHER' && item.department && (
                            <div>
                              <span className="text-on-surface-variant font-label">部门：</span>
                              <span className="text-on-surface">{item.department}</span>
                            </div>
                          )}
                          {item.type === 'TEACHER' && item.employeeNumber && (
                            <div>
                              <span className="text-on-surface-variant font-label">工号：</span>
                              <span className="text-on-surface">{item.employeeNumber}</span>
                            </div>
                          )}
                          {item.phone && (
                            <div>
                              <span className="text-on-surface-variant font-label">电话：</span>
                              <span className="text-on-surface">{item.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleViewDetail(item)}
                          className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-xl transition-colors font-medium whitespace-nowrap"
                        >
                          查看详情
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-on-surface-variant font-label">
                共 {totalPages} 页
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSearch(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium"
                >
                  上一页
                </button>
                <div className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl text-sm font-medium">
                  {currentPage + 1} / {totalPages}
                </div>
                <button
                  onClick={() => handleSearch(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-low transition-colors text-sm font-medium"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserSearchPage;
